import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { X, ArrowRightLeft, Loader2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { departmentsService } from '@/services/departments.service';
import { chatService } from '@/services/chat.service';
import { useChatStore } from '@/stores/chat-store';
import { Button, useToast } from '@caruso/ui';
import { cn } from '@caruso/ui';

interface TransferDialogProps {
  isOpen: boolean;
  onClose: () => void;
  conversationId: string;
}

export function TransferDialog({ isOpen, onClose, conversationId }: TransferDialogProps) {
  const [selectedDepartment, setSelectedDepartment] = useState('');
  const [selectedUser, setSelectedUser] = useState('');
  const [reason, setReason] = useState('');
  const [isTransferring, setIsTransferring] = useState(false);

  const { toast } = useToast();
  const { fetchConversations } = useChatStore();

  // Fetch departments
  const { data: departmentsData, isLoading: isLoadingDepartments } = useQuery({
    queryKey: ['departments'],
    queryFn: () => departmentsService.getAll(),
    enabled: isOpen,
  });

  // Fetch users of selected department
  const { data: departmentUsers, isLoading: isLoadingUsers } = useQuery({
    queryKey: ['department-users', selectedDepartment],
    queryFn: () => departmentsService.getUsers(selectedDepartment),
    enabled: !!selectedDepartment,
  });

  const departments = departmentsData || [];

  // Reset form when dialog opens/closes
  useEffect(() => {
    if (!isOpen) {
      setSelectedDepartment('');
      setSelectedUser('');
      setReason('');
    }
  }, [isOpen]);

  // Reset user when department changes
  useEffect(() => {
    setSelectedUser('');
  }, [selectedDepartment]);

  const handleTransfer = async () => {
    if (!selectedDepartment) {
      toast({
        title: 'Selecione um departamento',
        description: 'E necessario selecionar um departamento para transferir.',
        variant: 'destructive',
      });
      return;
    }

    setIsTransferring(true);
    try {
      await chatService.transferConversation(conversationId, {
        departmentId: selectedDepartment,
        userId: selectedUser || undefined,
        reason: reason || undefined,
      });

      toast({
        title: 'Conversa transferida',
        description: 'A conversa foi transferida com sucesso.',
        variant: 'success',
      });

      fetchConversations();
      onClose();
    } catch (error) {
      console.error('Error transferring conversation:', error);
      toast({
        title: 'Erro',
        description: 'Nao foi possivel transferir a conversa.',
        variant: 'destructive',
      });
    } finally {
      setIsTransferring(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50"
            onClick={onClose}
          />

          {/* Dialog */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="fixed inset-0 flex items-center justify-center z-50 p-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-brand-ink border border-brand-gold/20 rounded-xl w-full max-w-md shadow-2xl">
              {/* Header */}
              <div className="flex items-center justify-between p-4 border-b border-brand-gold/10">
                <div className="flex items-center gap-2">
                  <ArrowRightLeft className="w-5 h-5 text-brand-gold" />
                  <h2 className="text-lg font-serif text-brand-gold">Transferir Conversa</h2>
                </div>
                <button
                  onClick={onClose}
                  className="p-1 rounded-lg hover:bg-brand-gold/10 text-brand-ivory/50 hover:text-brand-gold transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Content */}
              <div className="p-4 space-y-4">
                {/* Department Select */}
                <div>
                  <label className="block text-sm font-medium text-brand-ivory mb-2">
                    Departamento *
                  </label>
                  <select
                    value={selectedDepartment}
                    onChange={(e) => setSelectedDepartment(e.target.value)}
                    disabled={isLoadingDepartments}
                    className={cn(
                      'w-full px-3 py-2 bg-brand-smoke/50 border border-brand-gold/10 rounded-lg',
                      'text-brand-ivory focus:outline-none focus:border-brand-gold/30',
                      'disabled:opacity-50'
                    )}
                  >
                    <option value="">Selecione um departamento</option>
                    {departments.map((dept) => (
                      <option key={dept.id} value={dept.id}>
                        {dept.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* User Select (optional) */}
                {selectedDepartment && (
                  <div>
                    <label className="block text-sm font-medium text-brand-ivory mb-2">
                      Atendente (opcional)
                    </label>
                    <select
                      value={selectedUser}
                      onChange={(e) => setSelectedUser(e.target.value)}
                      disabled={isLoadingUsers}
                      className={cn(
                        'w-full px-3 py-2 bg-brand-smoke/50 border border-brand-gold/10 rounded-lg',
                        'text-brand-ivory focus:outline-none focus:border-brand-gold/30',
                        'disabled:opacity-50'
                      )}
                    >
                      <option value="">Qualquer atendente disponivel</option>
                      {departmentUsers?.map((ud) => (
                        <option key={ud.user.id} value={ud.user.id}>
                          {ud.user.name} {ud.isManager && '(Gerente)'}
                        </option>
                      ))}
                    </select>
                    {isLoadingUsers && (
                      <p className="text-xs text-brand-ivory/50 mt-1">Carregando usuarios...</p>
                    )}
                  </div>
                )}

                {/* Reason */}
                <div>
                  <label className="block text-sm font-medium text-brand-ivory mb-2">
                    Motivo (opcional)
                  </label>
                  <textarea
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder="Descreva o motivo da transferencia..."
                    rows={3}
                    className={cn(
                      'w-full px-3 py-2 bg-brand-smoke/50 border border-brand-gold/10 rounded-lg',
                      'text-brand-ivory placeholder:text-brand-ivory/40',
                      'focus:outline-none focus:border-brand-gold/30',
                      'resize-none'
                    )}
                  />
                </div>
              </div>

              {/* Footer */}
              <div className="flex items-center justify-end gap-3 p-4 border-t border-brand-gold/10">
                <Button
                  variant="ghost"
                  onClick={onClose}
                  disabled={isTransferring}
                  className="!font-normal text-brand-gold"
                >
                  Cancelar
                </Button>
                <Button
                  onClick={handleTransfer}
                  disabled={!selectedDepartment || isTransferring}
                  className="bg-brand-gold text-brand-ink hover:bg-brand-gold/90"
                >
                  {isTransferring ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Transferindo...
                    </>
                  ) : (
                    <>
                      <ArrowRightLeft className="w-4 h-4 mr-2" />
                      Transferir
                    </>
                  )}
                </Button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
